/**
 * cameraController — the camera's client-side "brain", driven from the Event
 * Graph (blueprint). Module singleton (like movementComponent / animVariables):
 * a single useFrame (<CameraControllerSync> in Scene.jsx) calls apply(dt, ctx) once per
 * frame — no React state on the hot path (CLAUDE.md hard rule).
 *
 * TWO KINDS OF STATE, and the difference matters:
 *
 *  - RIG (this._rig: target/yaw/pitch/dist + limits) — the camera's pose. Transient, lives
 *    HERE, never in liveTuneStore. The graph rewrites it every frame from user input via
 *    addYaw/addPitch/addDolly/panTarget, so the store's per-key/per-event precedence has no
 *    meaning for it. Consequence: motion ops have NO applied/ignored return value.
 *    This rig replaces OrbitControls, which no longer runs in live mode; the
 *    window.__ORBIT_CONTROLS__ contract is served by cameraRigCompat instead.
 *
 *  - CONFIG (liveTuneStore.camera: fov/target/cage/seeThrough/follow/blend) — session
 *    settings. Set via setFov/setTarget/setCage/setSeeThrough/setFollow, each routing
 *    through liveTuneStore.setCameraFromBlueprint so a dev's LiveTunePanel override wins
 *    when touched this session (precedence: URL > panel > blueprint > default). These
 *    return whether the write applied. Panel-lock governs config; never motion.
 *
 * Blend/follow interpolation is controller-local transient state (never the store) so it
 * doesn't persist or masquerade as a dev setting.
 *
 * The pure module-scope helpers (clampToCage, smoothstep, lerp, dampFactor, rotateYawOffset,
 * autoSeeThroughStrength, rigToPosition, deriveRig, rigRight, rigUp) are THREE-free so they
 * unit-test against plain arrays; the _apply* methods only call `.set()` / assign
 * properties, so they test against a fake camera / controls too.
 */

import { liveTuneStore } from './liveTuneStore.js'
import { DEV_LOG } from './devLog.js'

// Fallback follow offset (behind + slightly above). Module const so the follow
// hot path doesn't allocate it each frame when follow.offset is unset.
const DEFAULT_FOLLOW_OFFSET = [0, 1.6, 3]

// Shared frozen empty config so apply()'s see-through path never allocates a fresh
// {} per frame when c.seeThrough is falsy (per-frame alloc ban, CLAUDE.md). Never mutated.
const EMPTY_SEE_THROUGH = Object.freeze({})

/**
 * Clamp a camera position to a world-space AABB CENTERED ON `target`, so the
 * camera can't dolly out past the box faces (splat walls). Ray from target
 * toward the camera, find the slab exit distance, clamp distance-from-target
 * to (exit - margin), floored at minDistance. This is the cage-clamp logic
 * that previously lived in the standalone CameraCage component.
 *
 * `target` is BOTH the ray origin and the box center — deliberately the same
 * point, not two independently-movable ones. A ray cast from a box's own
 * center always exits through a face, so there is no "target outside the
 * box" failure mode to guard against; an earlier revision let the caller pass
 * a box center different from the ray origin (for a LiveTune CAGE ▸ OFFSET
 * that shifted the box away from the live orbit target) and that is exactly
 * what made the clamp silently stop firing — the live, freely-pannable
 * target routinely ends up outside a box centered somewhere else, and the
 * guard above then bails on every frame. The fix is at the CALL SITE
 * (cameraController.apply()): it picks ONE stable reference point (the
 * avatar's position + the cage offset, not the pannable orbit target) and
 * passes that single point in as `target` here.
 *
 * @param {number[]} camPos      [x,y,z] camera world position
 * @param {number[]} target      [x,y,z] cage center AND ray origin
 * @param {number[]} halfExtents [hx,hy,hz]
 * @returns {number[]|null} the clamped [x,y,z], or null if no clamp is needed
 *   (camera already inside the allowed radius).
 */
export function clampToCage(camPos, target, halfExtents, margin = 0.5, minDistance = 0.6, out = null) {
  const [tx, ty, tz] = target
  const [hx, hy, hz] = halfExtents
  const min = [tx - hx, ty - hy, tz - hz]
  const max = [tx + hx, ty + hy, tz + hz]

  const dir = [camPos[0] - tx, camPos[1] - ty, camPos[2] - tz]
  const r = Math.hypot(dir[0], dir[1], dir[2])
  if (r < 1e-4) return null
  dir[0] /= r; dir[1] /= r; dir[2] /= r

  let tExit = Infinity
  for (let a = 0; a < 3; a++) {
    const d = dir[a]
    if (Math.abs(d) < 1e-6) continue
    const tNeg = (min[a] - target[a]) / d
    const tPos = (max[a] - target[a]) / d
    const tFar = Math.max(tNeg, tPos)
    if (tFar < tExit) tExit = tFar
  }

  const maxR = Math.max(tExit - margin, minDistance)
  if (r <= maxR) return null
  const rx = tx + dir[0] * maxR, ry = ty + dir[1] * maxR, rz = tz + dir[2] * maxR
  if (out) { out[0] = rx; out[1] = ry; out[2] = rz; return out }
  return [rx, ry, rz]
}

/** Linear interpolate a → b by t∈[0,1]. */
export function lerp(a, b, t) { return a + (b - a) * t }

/** Cubic smoothstep ease of t (clamped to [0,1]). */
export function smoothstep(t) { t = t < 0 ? 0 : t > 1 ? 1 : t; return t * t * (3 - 2 * t) }

/**
 * Frame-rate-independent damping fraction: how far to move toward a target this
 * frame given a per-frame-at-60fps retention factor `damping` (0 = snap to
 * target this frame, →1 = barely move / very smooth). Corrects for `dt` so a
 * follow feels the same at 30 and 60 fps.
 */
export function dampFactor(damping, dt) {
  const d = damping <= 0 ? 0 : damping >= 0.999 ? 0.999 : damping
  if (d === 0) return 1
  return 1 - Math.pow(d, Math.max(dt, 0) * 60)
}

/**
 * Below this, a momentum accumulator is zeroed outright instead of decaying
 * asymptotically. Not cosmetic: _applyRig's `moved` test is an exact float
 * compare, so a sub-visible residual keeps returning true for hundreds of
 * frames, and every true re-dispatches 'change' → OGSModel re-runs Isaac splat
 * inference. Three-stdlib gates its own change event on a 1e-6 epsilon for the
 * same reason. Units: radians (yaw/pitch), log-distance (dolly), metres (pan).
 */
const MOMENTUM_EPS = 1e-6

// Basis unit vectors for the head-offset stage (ENG-981): right / up / back
// respectively. Module consts so the per-frame basis rotation (rotateByQuat
// below) never allocates a fresh array literal. "back" is +Z because the
// camera looks down -Z, so subtracting back*s[2] with a positive s[2]
// (headTrackedCamera's "closer face" sign) moves the camera FORWARD.
const X_UNIT = [1, 0, 0]
const Y_UNIT = [0, 1, 0]
const Z_UNIT = [0, 0, 1]

/**
 * Rotate a local vector [x,y,z] by a quaternion-like {x,y,z,w} into `out`.
 * THREE-free (mirrors the file's convention — see header) so it unit-tests
 * directly against THREE.Vector3.applyQuaternion without importing three
 * here; the head-offset stage feeds it cam.quaternion (a real THREE
 * Quaternion has the same {x,y,z,w} shape). Allocation-free: writes into the
 * caller-supplied `out`, never returns a new array.
 */
export function rotateByQuat(q, v, out) {
  const { x, y, z, w } = q
  const vx = v[0], vy = v[1], vz = v[2]
  const tx = 2 * (y * vz - z * vy), ty = 2 * (z * vx - x * vz), tz = 2 * (x * vy - y * vx)
  out[0] = vx + w * tx + (y * tz - z * ty)
  out[1] = vy + w * ty + (z * tx - x * tz)
  out[2] = vz + w * tz + (x * ty - y * tx)
  return out
}

/**
 * Rotate a local-space camera offset [x,y,z] into world space by the avatar's
 * facing. forward = [sin(yaw),0,cos(yaw)] (+Z rest-forward, matches
 * movementComponent/characterGet); right = [cos(yaw),0,-sin(yaw)]. offset.z is
 * along the avatar's forward (positive = in front, so a front-facing
 * conversational avatar looks toward the camera), offset.x along its right.
 * Returns the offset unchanged if forward is missing (world/degenerate).
 */
export function rotateYawOffset(offset, forward, out = null) {
  const [ox, oy, oz] = offset
  if (!forward) {
    if (out) { out[0] = ox; out[1] = oy; out[2] = oz; return out }
    return [ox, oy, oz]
  }
  const fx = forward[0], fz = forward[2]
  const len = Math.hypot(fx, fz) || 1
  const nfx = fx / len, nfz = fz / len            // normalized forward
  // right = [nfz, 0, -nfx]  (identity at yaw=0: fwd=[0,0,1], right=[1,0,0])
  const rx = ox * nfz + oz * nfx
  const rz = ox * -nfx + oz * nfz
  if (out) { out[0] = rx; out[1] = oy; out[2] = rz; return out }
  return [rx, oy, rz]
}

// ── Camera rig math ──────────────────────────────────────────────────────────
// The rig replaces OrbitControls' internal spherical state. Conventions, fixed here
// once so every consumer agrees:
//   position = target + [sin(yaw)cos(pitch), sin(pitch), cos(yaw)cos(pitch)] * dist
//   yaw   = 0 puts the camera on +Z looking toward -Z (matches Scene.jsx's default
//           camera at [-0.5, 1.4, 6.5] and the +Z rest-forward convention used by
//           movementComponent / rotateYawOffset above).
//   pitch > 0 raises the camera above the target.
// THREE-free so it unit-tests against plain arrays, same as clampToCage.

/** Pitch is clamped just shy of the poles: at exactly ±90° the yaw axis degenerates. */
export const PITCH_LIMIT = Math.PI / 2 * 0.98

export function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v }

/** World position for a rig pose. */
export function rigToPosition(yaw, pitch, dist, target, out = null) {
  const cp = Math.cos(pitch)
  const x = target[0] + Math.sin(yaw) * cp * dist
  const y = target[1] + Math.sin(pitch) * dist
  const z = target[2] + Math.cos(yaw) * cp * dist
  if (out) { out[0] = x; out[1] = y; out[2] = z; return out }
  return [x, y, z]
}

/**
 * Inverse of rigToPosition: recover {yaw, pitch, dist} from a camera position + target.
 * This is what lets an OUTSIDE writer (frameAvatar's F hotkey, StreamAvatarModel's
 * reframe, CameraFloorSync) move the camera and have the rig adopt it instead of
 * overwriting it next frame — the same re-derivation real OrbitControls does on every
 * update() (`offset.copy(object.position).sub(target)`).
 *
 * Degenerate case: a camera directly above/below the target has no horizontal offset, so
 * yaw is atan2(0,0) — meaningless. Keep `prevYaw` there rather than snapping to 0, which
 * would spin the camera on a purely vertical reframe.
 */
export function deriveRig(camPos, target, prevYaw = 0) {
  const ox = camPos[0] - target[0], oy = camPos[1] - target[1], oz = camPos[2] - target[2]
  const dist = Math.hypot(ox, oy, oz)
  if (dist < 1e-6) return { yaw: prevYaw, pitch: 0, dist: 1e-6 }
  const horiz = Math.hypot(ox, oz)
  const yaw = horiz < 1e-6 ? prevYaw : Math.atan2(ox, oz)
  const pitch = Math.asin(clamp(oy / dist, -1, 1))
  return { yaw, pitch, dist }
}

/** Camera-right (screen X) in world space. Independent of pitch. */
export function rigRight(yaw, out = null) {
  const x = Math.cos(yaw), z = -Math.sin(yaw)
  if (out) { out[0] = x; out[1] = 0; out[2] = z; return out }
  return [x, 0, z]
}

/** Camera-up (screen Y) in world space, tilted by pitch. */
export function rigUp(yaw, pitch, out = null) {
  const sp = Math.sin(pitch), cp = Math.cos(pitch)
  const x = -Math.sin(yaw) * sp, y = cp, z = -Math.cos(yaw) * sp
  if (out) { out[0] = x; out[1] = y; out[2] = z; return out }
  return [x, y, z]
}

/**
 * See-through fade strength (0 = untouched splat, 1 = full fade) for the current
 * camera geometry. Manual `enabled` → always 1. `auto` uses a proximity / angle
 * heuristic — NOT true per-splat occlusion (there's no cheap ray-vs-splat test):
 * fade when the camera is close to the head, or below it looking up (through a
 * table etc.). Covers the dominant occlusion cases when orbiting near the face.
 */
export function autoSeeThroughStrength(seeThrough, camPos, headPos) {
  if (seeThrough?.enabled) return 1
  if (!seeThrough?.auto) return 0
  const dx = camPos[0] - headPos[0], dy = camPos[1] - headPos[1], dz = camPos[2] - headPos[2]
  const dist = Math.hypot(dx, dy, dz)
  const near = dist < (seeThrough.autoNear ?? 1.2)
  const below = (seeThrough.autoBelow ?? true) && camPos[1] < headPos[1] - 0.05
  return (near || below) ? 1 : 0
}

class CameraController {
  constructor() {
    this._camera = null
    this._controls = null
    this._seeThroughSink = null   // (camWorldPos[3], headWorldPos[3], strength) => void

    // ── Rig ──
    // The camera's own orbit state, replacing OrbitControls' internal spherical. Transient
    // controller state, NEVER the store: the graph rewrites it every frame from input, and
    // liveTuneStore's precedence machinery is per-key + per-event — it has no meaning for a
    // continuously-driven value. Consequence, deliberate: motion ops have no
    // applied/ignored (panel-locked) return. That contract stays with the CONFIG setters
    // (fov/cage/seeThrough/follow/limits), which is the only place it means anything.
    this._rig = { target: [0, 1.35, 0], yaw: 0, pitch: 0, dist: 3 }
    this._limits = { minDist: 0.6, maxDist: Infinity, minPitch: -PITCH_LIMIT, maxPitch: PITCH_LIMIT }
    // ── Momentum (orbit inertia) ──
    // Restores the glide drei's OrbitControls gave us for free (enableDamping defaults TRUE;
    // three-stdlib/controls/OrbitControls.js:234-236). Same accumulator shape: input adds
    // here, _applyRig commits `k` of it per frame and decays the rest, so the FULL delta is
    // still delivered — just spread over time instead of landing in one frame.
    //
    // Pose-side transient state, like _rig itself: never liveTuneStore (that's config, and
    // its per-key precedence has no meaning for a value rewritten every frame).
    //
    // Damping is per-axis and last-input-wins, because it arrives per-call from blueprint
    // node data (like `sensitivity`) rather than being a controller-wide constant. Zero
    // damping on an axis means that axis bypasses the accumulator entirely.
    //
    // CANCELLATION RULE: user input ADDS momentum; every other acquisition of camera
    // authority ZEROES it (ctl.enabled === false, syncFromCamera, setRigPose, follow).
    // "Most recent intent wins" — the same rule the motion setters already apply to
    // _rigPose.active. Without it a stand-down or an F-hotkey framing inherits a stale
    // glide and gets dragged off the pose it just acquired.
    this._momentum = {
      yaw: 0, pitch: 0, logDist: 0, pan: [0, 0, 0],
      dYaw: 0, dPitch: 0, dDolly: 0, dPan: 0,
    }
    this._rigSynced = false       // has the rig adopted the real camera yet?
    this._rigPose = { active: false, from: null, to: null, t: 0, dur: 0 }
    this._sRigPos = [0, 0, 0]     // scratch — rigToPosition result
    this._sRight = [0, 0, 0]
    this._sUp = [0, 0, 0]
    // Transient motion state (never persisted — lives here, not in the store).
    this._fovBlend = { from: 0, to: null, t: 0, dur: 0 }
    this._poseBlend = { active: false, fromPos: [0, 0, 0], toPos: [0, 0, 0], fromTgt: [0, 0, 0], toTgt: [0, 0, 0], t: 0, dur: 0 }
    // Eases OrbitControls' look-at from its current point to a new target goal
    // over blend.target ms when the target CONFIG changes (mode/vector value).
    // _targetChanged() detects that discrete change allocation-free; a moving
    // 'head' still tracks because the ease converges toward the LIVE resolved
    // point each frame.
    this._targetBlend = { active: false, from: [0, 0, 0], t: 0, dur: 0 }
    this._lastTargetMode = null
    this._lastTargetVec = [NaN, NaN, NaN]  // scratch for vector-target compare
    this._followActive = false
    this._followWarned = false
    this._stStrength = 0          // eased see-through strength (avoids on/off pop)
    // Per-frame scratch, reused across apply() so the hot path allocates nothing
    // (CLAUDE.md: no per-frame allocations on the render path).
    this._sTarget = [0, 0, 0]     // _resolveTargetPoint head-mode result
    this._sAvatar = [0, 0, 0]     // follow avatar-pos fallback
    this._sDisp = [0, 0, 0]       // follow rotated offset
    this._sDesired = [0, 0, 0]    // follow desired camera pos
    this._sCamPos = [0, 0, 0]     // cage / see-through camera pos
    this._sTgt = [0, 0, 0]        // orbit target (fallback anchor, no avatar)
    this._sHalf = [0, 0, 0]       // cage half-extents
    this._sAnchor = [0, 0, 0]     // cage's single reference point: anchor (avatar pos, or target fallback) + offset
    this._sClamp = [0, 0, 0]      // cage clamp result
    this._sHead = [0, 0, 0]       // see-through head-pos fallback

    // ── Head-tracked additive offset (ENG-981) ──
    // Neither RIG nor CONFIG (see the two kinds of state described above):
    // it's a post-pose COMPOSITION step layered on top of whichever owner
    // placed the camera this frame, and it is ONE-WAY — never read back into
    // _rig (the rig recomputes position from yaw/pitch/dist every frame, so
    // adopting this offset would compound it), never routed through
    // liveTuneStore. Wired from headTrackedCamera.js (feature -> controller),
    // never the reverse; see setHeadOffsetSource().
    this._headOffsetSource = null // headTrackedCamera's `source`, or null
    this._sHeadOff = [0, 0, 0]    // smoothed offset (right, up, back-negated)
    this._sHeadPrev = [0, 0, 0]   // previous frame's smoothed offset, for `moved`
    this._headLogAt = 0           // last ?debug=1 head-offset log timestamp (1 Hz throttle)
    this._headAppliedW = [0, 0, 0] // world-space translation the EDITOR path applied last frame (see applyHeadOffsetTo)
    this._sBasisR = [0, 0, 0]     // scratch — camera-right this frame
    this._sBasisU = [0, 0, 0]     // scratch — camera-up this frame
    this._sBasisB = [0, 0, 0]     // scratch — camera-back this frame
    this._offAxisApplied = false  // true while the projection matrix is lens-shifted
  }

  // --- wiring (called by CameraControllerSync) ---
  setCamera(c) { this._camera = c }
  setControls(c) { this._controls = c }
  /** Registered by the see-through helper (Phase 3) to receive per-frame
   *  camera + head world positions + fade strength. Kept out of the controller
   *  so the Spark dyno never leaks into the camera singleton. */
  setSeeThroughSink(fn) { this._seeThroughSink = fn }
  /**
   * Wired once from headTrackedCamera.js's module bottom (feature -> controller,
   * never the reverse). `src` is that module's `source` object — `{ target:
   * [x,y,z], mode, smoothing, active }`, `target` mutated in place — or null to
   * disable. Stored by reference, never copied: apply() must read the SAME
   * array the tracker's pose callback writes every frame.
   */
  setHeadOffsetSource(src) { this._headOffsetSource = src || null }

  /**
   * applyHeadOffsetTo / unapplyHeadOffsetFrom — the head-offset stage for a
   * camera apply() does NOT own: the editor viewport (and live's rig-less
   * fallback), where drei's OrbitControls drives the pose and apply() never
   * runs. Called per frame by Scene.jsx's <EditorHeadParallax>, mounted
   * wherever OrbitControls is (mutually exclusive with
   * <CameraControllerSync>, so the offset always has exactly one applier).
   * Same smoothing state as the apply() path (_sHeadOff is shared), so a
   * session survives a live↔edit flip mid-track without snapping. Honours the
   * same stand-down contract as apply(): a disabled controls object (the IBL
   * bake tool) is untouchable. Off-cost is a few comparisons per pass.
   *
   * THE SANDWICH IS LOAD-BEARING. three's OrbitControls.update() re-derives
   * its spherical orbit state from camera.position every call — it ADOPTS
   * whatever position it finds. A bare additive offset is therefore adopted
   * as the new base each frame and added AGAIN the next: the offset
   * compounds, and because it's applied along camera-local axes that rotate
   * as the orbit drifts, it spirals ("the camera spins crazy" — Andres,
   * found live 2026-08-13, first click of the rail button). So the editor
   * path brackets drei's update (useFrame priority -1): unapply at -2
   * strips exactly the world-space translation applied last frame (recorded
   * in _headAppliedW by _applyHeadOffset), drei adopts the CLEAN base, then
   * the default-priority apply adds the fresh offset. The swivel rotation
   * needs no unapply — update()'s own lookAt() rewrites orientation from
   * scratch. The rig path records nothing (apply() zeroes the record):
   * the rig recomputes position wholesale each frame, so a flip back to the
   * editor can never inherit a stale subtraction.
   */
  applyHeadOffsetTo(cam, ctl, dt) {
    const rec = this._headAppliedW
    rec[0] = 0; rec[1] = 0; rec[2] = 0
    if (!cam || !ctl || ctl.enabled === false) return false
    return this._applyHeadOffset(cam, ctl, dt || 0, rec)
  }

  /** The pre-OrbitControls half of the sandwich (see applyHeadOffsetTo). */
  unapplyHeadOffsetFrom(cam, ctl) {
    const a = this._headAppliedW
    if (a[0] === 0 && a[1] === 0 && a[2] === 0) return
    if (!cam || !ctl || ctl.enabled === false) {
      // Stand-down mid-session: the offset stays baked into whatever pose the
      // outside owner adopted (same one-time adoption the rig's
      // syncFromCamera does); dropping the record prevents a later
      // re-enable from subtracting it out of a pose it's no longer part of.
      a[0] = 0; a[1] = 0; a[2] = 0
      return
    }
    cam.position.x -= a[0]
    cam.position.y -= a[1]
    cam.position.z -= a[2]
    a[0] = 0; a[1] = 0; a[2] = 0
  }

  // --- node setters → write desired state (blueprint precedence) ---
  // blendMs eases the change over a duration (0 = snap, legacy behaviour).
  // Each returns whether its PRIMARY key actually applied (false = a panel/URL
  // touch this session took precedence and the write was dropped) — callers
  // (EventRuntime devLog, __TEST_CAMERA__) use this instead of assuming success.
  setFov(f, blendMs = 0) {
    const n = Number(f)
    if (!Number.isFinite(n)) return false
    const r = liveTuneStore.setCameraFromBlueprint({ fov: n, blend: { fov: Number(blendMs) || 0 } })
    return r.appliedKeys.includes('fov')
  }
  setTarget(t, blendMs = 0) { return liveTuneStore.setCameraFromBlueprint({ target: t, blend: { target: Number(blendMs) || 0 } }).appliedKeys.includes('target') }
  setCage(c)       { return liveTuneStore.setCameraFromBlueprint({ cage: c }).appliedKeys.includes('cage') }
  setSeeThrough(s) { return liveTuneStore.setCameraFromBlueprint({ seeThrough: s }).appliedKeys.includes('seeThrough') }
  setFollow(f)     { return liveTuneStore.setCameraFromBlueprint({ follow: f }).appliedKeys.includes('follow') }

  /**
   * Why the rig motion API (addYaw/addPitch/addDolly/panTarget) is currently inert, if it
   * is: 'stand-down' (controls.enabled === false — apply() returns before the rig runs),
   * 'follow' (follow owns the pose; apply() drains motion momentum every frame), else null.
   * Ordered to match apply()'s ownership chain, so the report names the ACTUAL owner.
   *
   * EDITOR feedback only. The runtime deliberately keeps executing motion nodes under
   * suppression (skipping them in EventRuntime would make the exec chain camera-state-
   * dependent), so without this the graph flashes a node as fired while the camera ignores
   * it — indistinguishable from a broken rig. Motion setters still return nothing; this is
   * a readable state, not a per-call verdict.
   */
  motionSuppressed() {
    if (this._controls && this._controls.enabled === false) return 'stand-down'
    if (liveTuneStore.getState().camera.follow?.enabled) return 'follow'
    return null
  }

  // ── Rig motion API (Camera Add Yaw / Pitch / Dolly / Pan Target nodes) ──
  // These mutate transient rig state directly and return nothing: see this._rig.

  // `damping` (0 = instant, →1 = long glide) is a per-CALL argument supplied from blueprint
  // node data, not controller state — exactly like `sensitivity`. The API default is 0 so a
  // scripted move still lands exactly where the author put it; the shipped INTERACTIVE feel
  // comes from the node defaults in EventRuntime. Callers reading getRig() straight after an
  // undamped call see the full delta, as they always have.

  addYaw(delta, damping = 0) {
    const d = Number(delta)
    if (!Number.isFinite(d) || d === 0) return
    const k = Number(damping) || 0
    if (k > 0) { this._momentum.yaw += d; this._momentum.dYaw = k }
    else this._rig.yaw += d
    this._rigPose.active = false   // explicit motion outranks an in-flight pose blend
  }

  addPitch(delta, damping = 0) {
    const d = Number(delta)
    if (!Number.isFinite(d) || d === 0) return
    const k = Number(damping) || 0
    // Damped pitch is deliberately NOT pre-clamped: the accumulator is unbounded by design
    // and _applyRig's pose clamp is the single authority. Pre-clamping here would discard
    // momentum the moment the pose touched a limit, killing the softened reversal you get
    // when you flick into the pole and pull back. The instant path keeps its clamp for
    // byte-exact back-compat.
    if (k > 0) { this._momentum.pitch += d; this._momentum.dPitch = k }
    else this._rig.pitch = clamp(this._rig.pitch + d, this._limits.minPitch, this._limits.maxPitch)
    this._rigPose.active = false
  }

  /**
   * Dolly is MULTIPLICATIVE (dist *= e^delta), not additive: a fixed wheel notch should
   * cover the same visual fraction of the distance whether you're 0.5m or 50m out. Additive
   * dolly crawls when far and slams into minDist when near.
   */
  addDolly(delta, damping = 0) {
    const d = Number(delta)
    if (!Number.isFinite(d) || d === 0) return
    const k = Number(damping) || 0
    // Momentum accumulates in LOG space — the only representation where damping composes
    // with a multiplicative dolly. Decaying exponents sum back to the original exponent, so
    // the glide converges on exactly dist·e^(total delta) and stays scale-invariant mid-glide.
    // Accumulating a multiplicative factor instead yields partial products that neither
    // converge to e^d nor hold scale invariance.
    if (k > 0) { this._momentum.logDist += d; this._momentum.dDolly = k }
    else this._rig.dist = clamp(this._rig.dist * Math.exp(d), this._limits.minDist, this._limits.maxDist)
    this._rigPose.active = false
  }

  /** Pan the look-at along camera right/up. Scaled by dist so it feels the same at any zoom. */
  panTarget(dx, dy, scale = 0.0015, damping = 0) {
    const x = Number(dx), y = Number(dy)
    if (!Number.isFinite(x) || !Number.isFinite(y) || (x === 0 && y === 0)) return
    const r = rigRight(this._rig.yaw, this._sRight)
    const u = rigUp(this._rig.yaw, this._rig.pitch, this._sUp)
    const k = this._rig.dist * scale
    // The camera basis and the dist-proportional gain resolve at INPUT time, then the
    // displacement is world-space from here on — a yaw change mid-glide does not bend an
    // in-flight pan. That's what three does (panOffset is a world vector, decayed without
    // re-steering), it's physically right for momentum, and it avoids re-deriving the basis
    // every frame. Re-resolving screen-space residuals per frame would curve the glide with
    // the camera: novel behaviour nobody asked for.
    const dmp = Number(damping) || 0
    const t = dmp > 0 ? this._momentum.pan : this._rig.target
    if (dmp > 0) this._momentum.dPan = dmp
    t[0] += r[0] * -x * k + u[0] * y * k
    t[1] += r[1] * -x * k + u[1] * y * k
    t[2] += r[2] * -x * k + u[2] * y * k
    this._rigPose.active = false
  }

  /** Set the rig's look-at directly (compat.update() adopting an outside target write). */
  setRigTarget(x, y, z) {
    const t = this._rig.target
    if (Number.isFinite(x)) t[0] = x
    if (Number.isFinite(y)) t[1] = y
    if (Number.isFinite(z)) t[2] = z
  }

  setRigLimits(patch = {}) {
    const l = this._limits
    if (Number.isFinite(patch.minDist)) l.minDist = Math.max(1e-3, patch.minDist)
    if (Number.isFinite(patch.maxDist)) l.maxDist = patch.maxDist
    if (Number.isFinite(patch.minPitch)) l.minPitch = clamp(patch.minPitch, -PITCH_LIMIT, PITCH_LIMIT)
    if (Number.isFinite(patch.maxPitch)) l.maxPitch = clamp(patch.maxPitch, -PITCH_LIMIT, PITCH_LIMIT)
    if (l.maxDist < l.minDist) l.maxDist = l.minDist
    if (l.maxPitch < l.minPitch) l.maxPitch = l.minPitch
    // Re-clamp the live pose so a tightened limit takes effect now, not on the next input.
    this._rig.dist = clamp(this._rig.dist, l.minDist, l.maxDist)
    this._rig.pitch = clamp(this._rig.pitch, l.minPitch, l.maxPitch)
  }

  /** Jump (or ease) the rig to an absolute pose. blendMs 0 = snap. */
  setRigPose(pose = {}, blendMs = 0) {
    const r = this._rig
    // An authored move must LAND on its destination. A live accumulator would be added on
    // top of the blend every frame and drift the endpoint.
    this._zeroMomentum()
    const to = {
      yaw: Number.isFinite(pose.yaw) ? pose.yaw : r.yaw,
      pitch: Number.isFinite(pose.pitch) ? clamp(pose.pitch, this._limits.minPitch, this._limits.maxPitch) : r.pitch,
      dist: Number.isFinite(pose.dist) ? clamp(pose.dist, this._limits.minDist, this._limits.maxDist) : r.dist,
      target: Array.isArray(pose.target) ? [pose.target[0], pose.target[1], pose.target[2]] : [r.target[0], r.target[1], r.target[2]],
    }
    const dur = Number(blendMs) || 0
    if (dur <= 0) {
      r.yaw = to.yaw; r.pitch = to.pitch; r.dist = to.dist
      r.target[0] = to.target[0]; r.target[1] = to.target[1]; r.target[2] = to.target[2]
      this._rigPose.active = false
      return
    }
    this._rigPose = {
      active: true, t: 0, dur,
      from: { yaw: r.yaw, pitch: r.pitch, dist: r.dist, target: [r.target[0], r.target[1], r.target[2]] },
      to,
    }
  }

  /**
   * Adopt the live camera + target into the rig. Called on entering live mode (else the
   * camera jumps on every mode switch) and by cameraRigCompat.update() when an outside
   * writer has moved the camera.
   */
  syncFromCamera() {
    const cam = this._camera
    if (!cam) return false
    const t = this._rig.target
    const d = deriveRig([cam.position.x, cam.position.y, cam.position.z], t, this._rig.yaw)
    this._rig.yaw = d.yaw
    this._rig.pitch = clamp(d.pitch, this._limits.minPitch, this._limits.maxPitch)
    // NOT clamped to limits: adopt what the outside writer asked for. The next apply()
    // clamps it — same as OrbitControls, which also clamps an out-of-band position on its
    // next update(). Documented behaviour, not a bug.
    this._rig.dist = Math.max(1e-3, d.dist)
    this._rigPose.active = false   // an external write outranks an in-flight blend
    // ...and outranks an in-flight glide, for the same reason. Without this, pressing F
    // mid-drag frames the avatar and the leftover momentum immediately un-frames it.
    this._zeroMomentum()
    this._rigSynced = true
    return true
  }

  /**
   * Discard pending glide. Called wherever something OTHER than user input takes the
   * camera, so that authority isn't immediately fought by leftover motion. Allocation-free
   * (scalar writes into the preallocated struct) — safe to call every frame.
   */
  _zeroMomentum() {
    const m = this._momentum
    m.yaw = 0; m.pitch = 0; m.logDist = 0
    m.pan[0] = 0; m.pan[1] = 0; m.pan[2] = 0
  }

  /** Read-only rig snapshot (tests + one-shot effect reads). Allocates — not hot-path. */
  getRig() {
    const r = this._rig, m = this._momentum
    return {
      yaw: r.yaw, pitch: r.pitch, dist: r.dist, target: [...r.target], limits: { ...this._limits },
      // Pending (uncommitted) glide. Exposed so callers can assert conservation —
      // committed + pending === total input — instead of guessing at an in-flight value.
      momentum: { yaw: m.yaw, pitch: m.pitch, logDist: m.logDist, pan: [...m.pan] },
    }
  }

  /**
   * Commit this frame's share of the pending glide onto the rig and decay the rest.
   *
   * Shape is three-stdlib's (OrbitControls.js:191-197 apply, 234-236 decay): commit
   * `k` of the remainder, retain `1 - k`. Because each frame takes a fraction OF THE
   * REMAINDER, the series converges on the full input delta — momentum changes WHEN the
   * motion arrives, never HOW MUCH. That's what keeps the gain assertions honest.
   *
   * Unlike three, `k` is dt-corrected via dampFactor, so the glide has the same wall-clock
   * length at 30/60/144fps. At 60fps dampFactor(0.95, 1/60) is 0.05 to within float rounding
   * (0.050000000000000044), i.e. this is
   * numerically identical to the old build at the refresh rate it was tuned on — and better
   * above it, where three's per-frame constant made the glide 2.4x shorter at 144Hz. The
   * repo already made this call for follow/see-through; the rig matches its own codebase.
   *
   * The rig pose is NOT clamped here — _applyRig clamps right after, and the accumulator
   * stays unbounded so residual motion decays AGAINST a limit rather than being discarded
   * at it (three does the same with sphericalDelta vs the polar clamp).
   */
  _integrateMomentum(r, dt) {
    const m = this._momentum
    if (m.yaw !== 0) {
      const k = dampFactor(m.dYaw, dt)
      r.yaw += m.yaw * k
      m.yaw *= 1 - k
      if (Math.abs(m.yaw) < MOMENTUM_EPS) m.yaw = 0
    }
    if (m.pitch !== 0) {
      const k = dampFactor(m.dPitch, dt)
      r.pitch += m.pitch * k
      m.pitch *= 1 - k
      if (Math.abs(m.pitch) < MOMENTUM_EPS) m.pitch = 0
    }
    if (m.logDist !== 0) {
      const k = dampFactor(m.dDolly, dt)
      r.dist *= Math.exp(m.logDist * k)
      m.logDist *= 1 - k
      if (Math.abs(m.logDist) < MOMENTUM_EPS) m.logDist = 0
    }
    const p = m.pan
    if (p[0] !== 0 || p[1] !== 0 || p[2] !== 0) {
      const k = dampFactor(m.dPan, dt)
      r.target[0] += p[0] * k; r.target[1] += p[1] * k; r.target[2] += p[2] * k
      p[0] *= 1 - k; p[1] *= 1 - k; p[2] *= 1 - k
      if (Math.abs(p[0]) < MOMENTUM_EPS && Math.abs(p[1]) < MOMENTUM_EPS && Math.abs(p[2]) < MOMENTUM_EPS) {
        p[0] = 0; p[1] = 0; p[2] = 0
      }
    }
  }

  /** Write the rig pose onto the camera. Returns true if the camera actually moved. */
  _applyRig(cam, ctl, dtMs) {
    const r = this._rig
    // Momentum is integrated BEFORE the pose blend, which is only safe because the two can
    // never be active at once: every momentum source clears _rigPose.active, and setRigPose
    // zeroes momentum first. Keep that true of any NEW momentum source added here, or a
    // blend will fight a glide for control of the same fields.
    this._integrateMomentum(r, dtMs / 1000)
    if (this._rigPose.active) {
      const pb = this._rigPose
      pb.t += dtMs
      const e = smoothstep(pb.dur > 0 ? Math.min(1, pb.t / pb.dur) : 1)
      r.yaw = lerp(pb.from.yaw, pb.to.yaw, e)
      r.pitch = lerp(pb.from.pitch, pb.to.pitch, e)
      r.dist = lerp(pb.from.dist, pb.to.dist, e)
      r.target[0] = lerp(pb.from.target[0], pb.to.target[0], e)
      r.target[1] = lerp(pb.from.target[1], pb.to.target[1], e)
      r.target[2] = lerp(pb.from.target[2], pb.to.target[2], e)
      if ((pb.dur > 0 ? pb.t / pb.dur : 1) >= 1) pb.active = false
    }
    r.pitch = clamp(r.pitch, this._limits.minPitch, this._limits.maxPitch)
    r.dist = clamp(r.dist, this._limits.minDist, this._limits.maxDist)
    const p = rigToPosition(r.yaw, r.pitch, r.dist, r.target, this._sRigPos)
    const moved = cam.position.x !== p[0] || cam.position.y !== p[1] || cam.position.z !== p[2]
    cam.position.set(p[0], p[1], p[2])
    ctl.target.set(r.target[0], r.target[1], r.target[2])
    // ORIENT the camera. Moving position alone leaves it staring in whatever direction it
    // last faced, so the subject slides out of frame as you orbit — and every numeric
    // assertion still passes, because the rig and the position are both correct. This is
    // the same final step OrbitControls.update() performs (`object.lookAt(target)`); the
    // rig replaced OrbitControls, so it inherits the obligation.
    cam.lookAt?.(r.target[0], r.target[1], r.target[2])
    return moved
  }

  // --- getter node ---
  getTransform() {
    const cam = this._camera, ctl = this._controls
    if (!cam || !ctl) return null
    return {
      location: [cam.position.x, cam.position.y, cam.position.z],
      rotation: [
        (cam.rotation?.x ?? 0) * 180 / Math.PI,
        (cam.rotation?.y ?? 0) * 180 / Math.PI,
        (cam.rotation?.z ?? 0) * 180 / Math.PI,
      ],
      target: [ctl.target.x, ctl.target.y, ctl.target.z],
      fov: cam.fov,
    }
  }

  /**
   * Allocation-free discrete target-config change detection, replacing a
   * per-frame `targetKey()` string build (CLAUDE.md: no per-frame allocation on
   * the camera apply path). Caches the last mode + vector componentwise and
   * returns whether the config changed since the previous call. 'head'/'fixed'
   * key on the mode alone; 'vector' also compares its coordinates.
   */
  _targetChanged(target) {
    const mode = target?.mode ?? 'fixed'
    let changed = mode !== this._lastTargetMode
    if (mode === 'vector' && Array.isArray(target.value)) {
      const v = target.value, lv = this._lastTargetVec
      if (v[0] !== lv[0] || v[1] !== lv[1] || v[2] !== lv[2]) changed = true
      lv[0] = v[0]; lv[1] = v[1]; lv[2] = v[2]
    }
    this._lastTargetMode = mode
    return changed
  }

  /**
   * Resolve the desired look-at point for the current target config, or null to
   * leave the target to drei's prop (orbit + fixed).
   */
  _resolveTargetPoint(target, ctx) {
    const floor = ctx.stageFloorY || 0
    const mode = target?.mode ?? 'fixed'
    if (mode === 'head') {
      const s = this._sTarget
      if (ctx.headProbe) { s[0] = ctx.headProbe[0]; s[1] = ctx.headProbe[1]; s[2] = ctx.headProbe[2] }
      else { s[0] = 0; s[1] = 1.5 + floor; s[2] = 0 }
      return s
    }
    if (mode === 'vector' && Array.isArray(target.value)) return target.value
    return null // 'fixed'
  }

  /** Ease camera.fov toward the desired value over durMs (0 = snap). */
  _applyFov(cam, targetFov, durMs, dtMs) {
    const fb = this._fovBlend
    if (targetFov !== fb.to) { fb.from = cam.fov; fb.to = targetFov; fb.t = 0; fb.dur = durMs }
    if (cam.fov === fb.to) return
    fb.t += dtMs
    const a = fb.dur > 0 ? Math.min(1, fb.t / fb.dur) : 1
    const nv = lerp(fb.from, fb.to, smoothstep(a))
    if (cam.fov !== nv) { cam.fov = nv; cam.updateProjectionMatrix?.() }
  }

  /**
   * Follow / framing driver — keeps the avatar framed as it moves. Overrides the
   * rig's camera pose + target while enabled and disables user orbit-rotate.
   * offset is world-space, or (space 'local') rotated by the avatar's facing so
   * the camera trails on turns. Position damps toward the desired pose (dt-corrected).
   */
  _applyFollow(cam, ctl, follow, ctx, dt) {
    const floor = ctx.stageFloorY || 0
    if (!ctx.avatarPos && !this._followWarned) {
      console.warn('[cameraController] follow enabled but avatar position unavailable — framing the fixed head probe (stationary).')
      this._followWarned = true
    }
    let avatarPos = ctx.avatarPos
    if (!avatarPos) {
      avatarPos = this._sAvatar
      avatarPos[0] = ctx.headProbe ? ctx.headProbe[0] : 0
      avatarPos[1] = floor
      avatarPos[2] = ctx.headProbe ? ctx.headProbe[2] : 0
    }
    const headY = (ctx.headProbe ? ctx.headProbe[1] : 1.5 + floor) + (follow.targetOffsetY || 0)
    const offset = Array.isArray(follow.offset) ? follow.offset : DEFAULT_FOLLOW_OFFSET
    const disp = follow.space === 'world' ? offset : rotateYawOffset(offset, ctx.avatarForward, this._sDisp)
    const desired = this._sDesired
    desired[0] = avatarPos[0] + disp[0]; desired[1] = avatarPos[1] + disp[1]; desired[2] = avatarPos[2] + disp[2]
    const k = dampFactor(follow.damping ?? 0.12, dt)
    cam.position.set(
      lerp(cam.position.x, desired[0], k),
      lerp(cam.position.y, desired[1], k),
      lerp(cam.position.z, desired[2], k),
    )
    ctl.target.set(avatarPos[0], headY, avatarPos[2])
    // Orient, for the same reason as _applyRig: OrbitControls used to do this in update(),
    // and follow no longer runs through it. Previously follow relied on drei's update()
    // re-aiming the camera behind its back.
    cam.lookAt?.(avatarPos[0], headY, avatarPos[2])
    // No "disable rotate" flag needed: while follow owns the camera the rig simply isn't
    // applied, so user input has no effect, and syncFromCamera() on exit adopts wherever
    // follow parked it. (The old code set ctl.enableRotate=false, which OrbitControls read;
    // nothing reads it now, so keeping it would be vestigial state that looks load-bearing.)
    this._followActive = true
  }

  /**
   * Additive head-tracked camera offset (ENG-981) — see the constructor
   * comment for why this is neither RIG nor CONFIG state. Runs after
   * whichever owner (rig or follow) placed cam.position this frame and adds
   * a translation (and, in off-axis mode, a lens-shift) on top of it; never
   * reads or writes this._rig.
   *
   * `src` is headTrackedCamera's `source` object (or null): `{ target:
   * [x,y,z], mode, smoothing, active }`, `target` already fully mapped
   * upstream (mirror/scale/invert/clamp) — this stage only smooths and
   * applies it.
   *
   * @returns {boolean} whether this stage's own contribution actually
   *   changed this frame — mirrors _applyRig's `moved` contract so apply()
   *   can OR it into the same 'change' dispatch.
   */
  _applyHeadOffset(cam, ctl, dt, recordInto = null) {
    const src = this._headOffsetSource
    const s = this._sHeadOff
    const active = !!(src && src.active)

    // Off guard: nothing pending and nothing to animate. This is the ENTIRE
    // cost with the feature off/unwired — up to four short-circuited
    // comparisons, no allocation — which is the ticket's "no measurable
    // regression when disabled" requirement. Still
    // restores a stray off-axis skew (one-shot) so a source that deactivates
    // with an already-settled offset can't leave the projection matrix
    // permanently skewed.
    if (!active && s[0] === 0 && s[1] === 0 && s[2] === 0) {
      if (this._offAxisApplied) {
        cam.updateProjectionMatrix()
        this._offAxisApplied = false
      }
      return false
    }

    // Smooth toward the mapped target — or toward zero once the source has
    // deactivated / lost the face, since headTrackedCamera zeroes
    // source.target on every teardown path. "Decay to base" therefore falls
    // out of this same smoothing continuing to run, not a separate path.
    const tx = src ? src.target[0] : 0
    const ty = src ? src.target[1] : 0
    const tz = src ? src.target[2] : 0
    const k = dampFactor(src?.smoothing ?? 0.85, dt)
    s[0] += (tx - s[0]) * k
    s[1] += (ty - s[1]) * k
    s[2] += (tz - s[2]) * k
    // Snap to exact zero once settled — same MOMENTUM_EPS rationale as the
    // rig's glide: a sub-visible residual here would keep `moved` true
    // forever, and every true re-dispatches 'change' -> OGSModel re-runs
    // Isaac splat appearance inference for a camera that isn't really moving.
    if (tx === 0 && Math.abs(s[0]) < MOMENTUM_EPS) s[0] = 0
    if (ty === 0 && Math.abs(s[1]) < MOMENTUM_EPS) s[1] = 0
    if (tz === 0 && Math.abs(s[2]) < MOMENTUM_EPS) s[2] = 0

    // Basis from the camera's LIVE orientation — valid after lookAt() in
    // both the rig and follow paths above, so this composes on top of
    // whichever one ran this frame without re-deriving its own orientation.
    // Orientation is deliberately left untouched: a pure eye translation
    // with fixed orientation IS the pan-mode parallax.
    const r = rotateByQuat(cam.quaternion, X_UNIT, this._sBasisR)
    const u = rotateByQuat(cam.quaternion, Y_UNIT, this._sBasisU)
    const b = rotateByQuat(cam.quaternion, Z_UNIT, this._sBasisB)

    // Off-axis must read the PRE-translation distance to the look-at point —
    // that's what keeps the focal plane fixed while the eye translates (true
    // parallax, not a pan).
    const wantOffAxis = !!(src && src.mode === 'offAxis')
    let dist
    if (wantOffAxis) {
      // Floored the same way the cage-clamp adoption floors its own distance
      // (this file, `Math.max(1e-3, Math.hypot(...))` above) — a camera that
      // ever coincides with the look-at target (a plausible transient during
      // a target/follow transition) would otherwise divide by ~0 below and
      // bake +/-Infinity or NaN into the projection matrix, silently.
      dist = Math.max(1e-3, Math.hypot(
        cam.position.x - ctl.target.x,
        cam.position.y - ctl.target.y,
        cam.position.z - ctl.target.z,
      ))
    }

    const dx = r[0] * s[0] + u[0] * s[1] - b[0] * s[2]
    const dy = r[1] * s[0] + u[1] * s[1] - b[1] * s[2]
    const dz = r[2] * s[0] + u[2] * s[1] - b[2] * s[2]
    cam.position.x += dx
    cam.position.y += dy
    cam.position.z += dz
    // recordInto: the editor path's compounding guard (see applyHeadOffsetTo)
    // remembers the exact world-space translation added this frame so the
    // pre-OrbitControls pass can strip it again. The rig path passes nothing —
    // it recomputes position wholesale each frame, so there is nothing to undo.
    if (recordInto) { recordInto[0] = dx; recordInto[1] = dy; recordInto[2] = dz }

    // Swivel: a small camera rotation TOWARD the subject, proportional to the
    // applied offset (radians per metre) — the munrocket/parallax-effect
    // exaggeration knob, default 0 (pure translation stays the physically
    // correct behaviour). Signs compensate the translation (moved right ->
    // look left, moved up -> look down) so the subject re-centres, which is
    // what makes the effect read strongly. Composed AFTER lookAt like the
    // translation above, so per-frame post-multiplication can never compound
    // (_applyRig/_applyFollow rewrite orientation every frame); decay falls
    // out of s -> 0 exactly like the translation. Post-multiply = rotation
    // about the camera's OWN axes, done in scalar math because this file is
    // deliberately THREE-free (see header) — q' = q ⊗ (q_yaw ⊗ q_pitch).
    const swivel = src ? (src.swivel ?? 0) : 0
    if (swivel !== 0 && (s[0] !== 0 || s[1] !== 0)) {
      const halfYaw = (swivel * s[0]) / 2
      const halfPitch = (-swivel * s[1]) / 2
      const cy = Math.cos(halfYaw), sy = Math.sin(halfYaw)
      const cp = Math.cos(halfPitch), sp = Math.sin(halfPitch)
      const rx = cy * sp, ry = sy * cp, rz = -sy * sp, rw = cy * cp
      const q = cam.quaternion
      const qx = q.x, qy = q.y, qz = q.z, qw = q.w
      q.x = qw * rx + qx * rw + qy * rz - qz * ry
      q.y = qw * ry + qy * rw + qz * rx - qx * rz
      q.z = qw * rz + qz * rw + qx * ry - qy * rx
      q.w = qw * rw - qx * rx - qy * ry - qz * rz
    }

    if (wantOffAxis) {
      // _applyFov already ran this frame, so rebuild from the LIVE cam.fov
      // every active frame rather than caching a stale skewed matrix.
      const near = cam.near, h = (near * Math.tan(cam.fov * Math.PI / 360)) / (cam.zoom || 1)
      const w = h * cam.aspect
      const sx = -s[0] * near / dist, sy = -s[1] * near / dist
      cam.projectionMatrix.makePerspective(-w + sx, w + sx, h + sy, -h + sy, near, cam.far)
      cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert()
      this._offAxisApplied = true
    } else if (this._offAxisApplied) {
      // Mode flipped away from 'offAxis' (or deactivated with a residual s
      // not yet at exact zero) — restore the symmetric frustum. Must not be
      // skippable, or the camera is left permanently skewed.
      cam.updateProjectionMatrix()
      this._offAxisApplied = false
    }

    // ?debug=1 diagnostics, 1 Hz: the applied offset, the active mode, and —
    // when off-axis — the frustum shift actually baked into the projection
    // matrix, so "offAxis looks the same as pan" can be settled by reading
    // the shift magnitude instead of eyeballing pixels. Gated on DEV_LOG
    // BEFORE any string is built (the template literal itself allocates).
    if (DEV_LOG) {
      const now = performance.now()
      if (now - this._headLogAt > 1000) {
        this._headLogAt = now
        let msg = `[head-track] apply mode=${src?.mode ?? 'decay'} offset=[${s[0].toFixed(3)}, ${s[1].toFixed(3)}, ${s[2].toFixed(3)}]m`
        if (swivel !== 0) msg += ` swivel=${(swivel * s[0] * 57.2958).toFixed(1)}°/${(-swivel * s[1] * 57.2958).toFixed(1)}°`
        if (wantOffAxis) {
          const near = cam.near
          msg += ` offAxis dist=${dist.toFixed(2)}m lensShift=[${(-s[0] * near / dist).toFixed(4)}, ${(-s[1] * near / dist).toFixed(4)}]`
        }
        console.log(msg)
      }
    }

    // moved: true only when THIS stage's own contribution actually changed —
    // a settled offset must stop dispatching 'change'.
    const prev = this._sHeadPrev
    const moved =
      Math.abs(s[0] - prev[0]) > MOMENTUM_EPS ||
      Math.abs(s[1] - prev[1]) > MOMENTUM_EPS ||
      Math.abs(s[2] - prev[2]) > MOMENTUM_EPS
    prev[0] = s[0]; prev[1] = s[1]; prev[2] = s[2]
    return moved
  }

  /**
   * Per-frame apply. ctx = { stageFloorY, headProbe, avatarPos, avatarForward }.
   *
   * OWNERSHIP RULE — exactly one owner of camera.position per frame, in this order:
   *
   *   1. controls.enabled === false  → stand down entirely (bake tool)
   *   2. follow.enabled              → follow owns position + target
   *   3. otherwise                   → the rig owns position; target mode owns the look-at
   *   then, composed on top: cage clamp → see-through sink → dispatch 'change' if moved
   */
  apply(dt, ctx = {}) {
    const cam = this._camera, ctl = this._controls
    if (!cam || !ctl) return

    // (1) An outside owner has taken the camera. bake_ibl.mjs sets this so its per-face
    //     lookAt() isn't overwritten every frame — without honouring it, every cube face
    //     bakes the same view and the IBL asset is silently wrong.
    //     A stand-down must not STORE energy: apply() returns before _applyRig, so without
    //     this the accumulator freezes untouched and discharges a stale glide onto whatever
    //     pose the bake tool restored on re-enable.
    if (ctl.enabled === false) { this._zeroMomentum(); return }

    const c = liveTuneStore.getState().camera
    const dtMs = Math.max(0, dt || 0) * 1000

    // fov is independent of who owns the pose.
    this._applyFov(cam, c.fov, c.blend?.fov ?? 0, dtMs)

    let moved = false

    // (2) follow / framing — continuous; overrides the rig pose while enabled.
    if (c.follow?.enabled) {
      this._applyFollow(cam, ctl, c.follow, ctx, dt || 0)
      // Drained EVERY follow frame, not once on entry: the blueprint's camera motion nodes
      // keep firing while follow owns the pose, so input would silently pile up and dump a
      // glide the instant follow released. (Follow exit also runs syncFromCamera below.)
      this._zeroMomentum()
      this._followActive = true
      moved = true
    } else {
      if (this._followActive) {
        // Leaving follow: adopt wherever follow parked the camera into the rig, so control
        // resumes from the current view instead of snapping back to the pre-follow pose.
        // One-shot — deriveRig allocates, so it must not run per frame.
        this._followActive = false
        this.syncFromCamera()
      }

      // (3a) target mode may drive the rig's look-at ('head' tracks the avatar, 'vector'
      //      pins a point, 'fixed' leaves the target to the rig / user panning).
      //      blend.target > 0 eases to a new goal instead of cutting.
      if (c.target && c.target.mode && c.target.mode !== 'fixed') {
        const pt = this._resolveTargetPoint(c.target, ctx)
        if (pt) {
          // Target mode OWNS the look-at, so it is one of the "every other acquisition of
          // camera authority" cases the cancellation rule covers — and the only one that
          // isn't obvious, because it takes the target rather than the whole pose. Without
          // this, a pan glide under head-tracking is neither delivered nor cancelled: each
          // frame the commit lands on the tracked point and the next frame's resolve throws
          // it away, so it degenerates into a decaying wobble. Runs before _applyRig, so the
          // pending pan never reaches the rig.
          const mp = this._momentum.pan
          mp[0] = 0; mp[1] = 0; mp[2] = 0
          const t = this._rig.target
          if (this._targetChanged(c.target)) {
            const dur = c.blend?.target ?? 0
            const tb = this._targetBlend
            if (dur > 0) {
              tb.active = true; tb.t = 0; tb.dur = dur
              tb.from[0] = t[0]; tb.from[1] = t[1]; tb.from[2] = t[2]
            } else {
              tb.active = false
            }
          }
          const tb = this._targetBlend
          if (tb.active) {
            tb.t += dtMs
            // Ease from the captured start toward the LIVE pt, so a moving head converges
            // rather than lagging a stale snapshot.
            const e = smoothstep(tb.dur > 0 ? Math.min(1, tb.t / tb.dur) : 1)
            t[0] = lerp(tb.from[0], pt[0], e); t[1] = lerp(tb.from[1], pt[1], e); t[2] = lerp(tb.from[2], pt[2], e)
            if ((tb.dur > 0 ? tb.t / tb.dur : 1) >= 1) tb.active = false
          } else {
            t[0] = pt[0]; t[1] = pt[1]; t[2] = pt[2]
          }
        }
      }

      // (3b) the rig owns the camera pose.
      moved = this._applyRig(cam, ctl, dtMs)
    }

    // (4) cage clamp (folds CameraCage) — recompute AFTER the target moved.
    //     Skipped while following: follow deliberately places the camera, so the
    //     orbit-limiting cage must not fight it. (Rig limits bound the dolly for every
    //     rig, so the cage only ever confines the orbit — it never truncates an
    //     authored pose.)
    //     The clamp writes the camera directly, then the rig ADOPTS it: without the
    //     sync the rig would re-place the camera at its unclamped dist next frame and
    //     the two would fight forever.
    if (c.cage?.enabled && !c.follow?.enabled) {
      const cp = this._sCamPos, tg = this._sTgt, hf = this._sHalf, anc = this._sAnchor
      cp[0] = cam.position.x; cp[1] = cam.position.y; cp[2] = cam.position.z
      tg[0] = ctl.target.x; tg[1] = ctl.target.y; tg[2] = ctl.target.z
      hf[0] = c.cage.hx; hf[1] = c.cage.hy; hf[2] = c.cage.hz
      // clampToCage's single reference point (both ray origin AND box
      // center) is a STABLE anchor + the cage's own offset (LiveTune CAGE ▸
      // OFFSET) — deliberately NOT the live orbit target (`tg`), which
      // right-drag pans freely. Using the pannable target as that single
      // point would drag the whole box along with every pan; using it as
      // just the ray origin while centering the box elsewhere (a prior
      // revision of this code) made the clamp silently stop firing whenever
      // the two drifted apart — which is routine, not an edge case. The
      // avatar's own world position doesn't move on a pan (only when it
      // walks), so it's the anchor whenever it's known; with no avatar in
      // context (e.g. an editor scene) this falls back to the orbit target,
      // same as the cage's pre-offset behaviour.
      if (ctx.avatarPos) {
        anc[0] = ctx.avatarPos[0]; anc[1] = ctx.headProbe ? ctx.headProbe[1] : tg[1]; anc[2] = ctx.avatarPos[2]
      } else {
        anc[0] = tg[0]; anc[1] = tg[1]; anc[2] = tg[2]
      }
      anc[0] += c.cage.ox ?? 0; anc[1] += c.cage.oy ?? 0; anc[2] += c.cage.oz ?? 0
      const clamped = clampToCage(cp, anc, hf, c.cage.margin, 0.6, this._sClamp)
      if (clamped) {
        cam.position.set(clamped[0], clamped[1], clamped[2])
        // Cheap targeted adoption — just the distance. A full syncFromCamera() here would
        // allocate every frame the cage bites; the clamp only ever moves the camera along
        // the target→camera ray, so yaw/pitch are unchanged by construction.
        this._rig.dist = Math.max(1e-3, Math.hypot(clamped[0] - tg[0], clamped[1] - tg[1], clamped[2] - tg[2]))
        moved = true
      }
    }

    // (5) head-tracked additive offset (ENG-981) — composed on top of whichever owner
    //     placed the camera this frame. ONE-WAY: never adopted into the rig (the rig
    //     recomputes from yaw/pitch/dist each frame, so this cannot compound), never
    //     stored. Decays to zero when the source deactivates, so the base camera is
    //     bit-identical with the feature off.
    // Rig path records no applied-offset (nothing to unapply — the rig
    // rewrites position wholesale each frame); zeroing here also clears any
    // record left by a live↔edit flip, so the editor's unapply pass can
    // never subtract a translation the rig already discarded.
    this._headAppliedW[0] = 0; this._headAppliedW[1] = 0; this._headAppliedW[2] = 0
    if (this._applyHeadOffset(cam, ctl, dt || 0)) moved = true

    // (g) see-through — eased fade strength + camera/head world positions to the
    //     Spark modifier (Phase 3). No-op until a sink is registered. The head
    //     tracks the avatar's LIVE world XZ (ctx.avatarPos) when available so the
    //     fade follows a moving avatar, not the fixed origin probe — head height
    //     stays at the probe's Y (moveToLocation only translates in XZ), same
    //     convention as _applyFollow's look-at.
    if (this._seeThroughSink) {
      const camPos = this._sCamPos
      camPos[0] = cam.position.x; camPos[1] = cam.position.y; camPos[2] = cam.position.z
      const head = this._sHead
      const probeY = ctx.headProbe ? ctx.headProbe[1] : 1.5 + (ctx.stageFloorY || 0)
      if (ctx.avatarPos) {
        head[0] = ctx.avatarPos[0]; head[1] = probeY; head[2] = ctx.avatarPos[2]
      } else {
        head[0] = ctx.headProbe ? ctx.headProbe[0] : 0
        head[1] = probeY
        head[2] = ctx.headProbe ? ctx.headProbe[2] : 0
      }
      const target = autoSeeThroughStrength(c.seeThrough || EMPTY_SEE_THROUGH, camPos, head)
      this._stStrength += (target - this._stStrength) * dampFactor(0.8, dt || 0)
      this._seeThroughSink(camPos, head, this._stStrength)
    }

    // (6) Notify 'change' listeners — the OGS eye branch re-runs its view-dependent pass off
    //     this, so a missing dispatch freezes eye colour mid-drag. (It is a cheap path, not
    //     the only one: view-dependent consumers also watch the camera per frame, because
    //     this object's identity swaps under them — src/ogs/cameraWatch.ts.) Only on
    //     an actual move: OrbitControls' own contract, and re-running inference on a still
    //     camera would burn GPU for nothing.
    if (moved) ctl.notifyChange?.()
  }
}

export const cameraController = new CameraController()

if (typeof window !== 'undefined') {
  // Test/console handle. Do NOT rename window.__ORBIT_CONTROLS__ (bake tool).
  window.__CAMERA_CONTROLLER__ = {
    setFov: (f, blendMs) => cameraController.setFov(f, blendMs),
    setTarget: (t, blendMs) => cameraController.setTarget(t, blendMs),
    setCage: (c) => cameraController.setCage(c),
    setSeeThrough: (s) => cameraController.setSeeThrough(s),
    setFollow: (f) => cameraController.setFollow(f),
    getTransform: () => cameraController.getTransform(),
    motionSuppressed: () => cameraController.motionSuppressed(),
    _controller: cameraController,
  }
  // Test/console helper — routes through the SAME setters as the panel and
  // blueprint nodes (never a parallel path), so a test exercising this proves
  // the one true code path. Returns the setter's applied/ignored boolean.
  window.__TEST_CAMERA__ = (action, opts = {}) => {
    switch (action) {
      case 'fov':        return cameraController.setFov(opts.value ?? opts.fov, opts.blendMs)
      case 'target':     return cameraController.setTarget(opts.value ?? opts.target ?? opts, opts.blendMs)
      case 'cage':       return cameraController.setCage(opts.value ?? opts.cage ?? opts)
      case 'seeThrough': return cameraController.setSeeThrough(opts.value ?? opts.seeThrough ?? opts)
      case 'follow':     return cameraController.setFollow(opts.value ?? opts.follow ?? opts)
      default:
        console.warn(`[__TEST_CAMERA__] unknown action "${action}"`)
        return false
    }
  }
}
